<template>
  <div class="modal fade" id="payrollDetailsModal" tabindex="-1" aria-labelledby="payrollDetailsModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="payrollDetailsModalLabel">Payroll Period Details</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" @click="closeModalFromEvent($event)" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary me-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading payroll details...</p>
          </div>

          <div v-else-if="!periodDetails" class="text-center py-5">
            <i class="mdi mdi-alert-circle-outline font-size-48 text-danger"></i>
            <p class="mt-2">Failed to load payroll details.</p>
          </div>

          <div v-else>
            <!-- Period header information -->
            <div class="card mb-4">
              <div class="card-body">
                <div class="row">
                  <div class="col-md-8">
                    <h4 class="card-title">{{ periodDetails.period_name }}</h4>
                    <p class="text-muted mb-0">
                      <span class="badge" :class="getStatusBadgeClass(periodDetails.status)">
                        {{ periodDetails.status }}
                      </span>
                    </p>
                  </div>
                  <div class="col-md-4 text-md-end">
                    <button
                        v-if="canProcessPeriod(periodDetails)"
                        @click="processPeriod"
                        class="btn btn-primary btn-sm me-2"
                    >
                      <i class="mdi mdi-play me-1"></i> Process
                    </button>
                    <button
                        v-if="canLockPeriod(periodDetails)"
                        @click="lockPeriod"
                        class="btn btn-warning btn-sm me-2"
                    >
                      <i class="mdi mdi-lock me-1"></i> Lock
                    </button>
                    <button
                        v-if="canApprovePeriod(periodDetails)"
                        @click="approvePeriod"
                        class="btn btn-success btn-sm me-2"
                    >
                      <i class="mdi mdi-check me-1"></i> Approve
                    </button>
                    <button
                        v-if="canMarkPaidPeriod(periodDetails)"
                        @click="markAsPaid"
                        class="btn btn-info btn-sm me-2"
                    >
                      <i class="mdi mdi-cash me-1"></i> Mark as Paid
                    </button>
                    <button
                        v-if="canClosePeriod(periodDetails)"
                        @click="closePeriod"
                        class="btn btn-secondary btn-sm"
                    >
                      <i class="mdi mdi-archive me-1"></i> Close
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Variance Analysis & IPPD -->
            <div class="card mb-4">
              <div class="card-header bg-light">
                <h5 class="card-title mb-0">
                  <i class="mdi mdi-chart-diff me-2"></i>Variance Analysis & IPPD (Treasury Circular)
                </h5>
              </div>
              <div class="card-body">
                <div class="row align-items-end mb-3">
                  <div class="col-md-4">
                    <label class="form-label">Compare with period</label>
                    <select v-model="comparePeriodId" @change="loadVarianceAnalysis" class="form-select form-select-sm">
                      <option value="">Previous period / None</option>
                      <option v-for="p in otherPeriodsForCompare" :key="p.id" :value="p.id">{{ p.period_name }}</option>
                    </select>
                  </div>
                  <div class="col-md-4">
                    <button type="button" class="btn btn-outline-primary btn-sm" @click="exportIPPD" :disabled="ippdExporting || !periodDetails.employee_count">
                      <span v-if="ippdExporting" class="spinner-border spinner-border-sm me-1"></span>
                      <i class="mdi mdi-file-export me-1"></i>Export IPPD (CSV)
                    </button>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label d-block">Import IPPD</label>
                    <input type="file" ref="ippdFileInput" accept=".csv" @change="onIppdFileSelect" class="form-control form-control-sm d-inline-block w-auto me-1">
                    <button type="button" class="btn btn-outline-secondary btn-sm" @click="importIPPD" :disabled="!ippdImportFile">Validate</button>
                  </div>
                </div>
                <div v-if="varianceData" class="table-responsive">
                  <table class="table table-sm table-bordered mb-0">
                    <thead><tr><th>Item</th><th>Current</th><th>Compare</th><th>Variance</th></tr></thead>
                    <tbody>
                      <tr><td>Gross</td><td>{{ formatCurrency(varianceData.currentSummary?.gross) }}</td><td>{{ formatCurrency(varianceData.compareSummary?.gross) }}</td><td :class="varianceClass(varianceData.variance?.gross_salary)">{{ formatCurrency(varianceData.variance?.gross_salary) }}</td></tr>
                      <tr><td>Deductions</td><td>{{ formatCurrency(varianceData.currentSummary?.deductions) }}</td><td>{{ formatCurrency(varianceData.compareSummary?.deductions) }}</td><td :class="varianceClass(-varianceData.variance?.total_deductions)">{{ formatCurrency(varianceData.variance?.total_deductions) }}</td></tr>
                      <tr><td>Tax</td><td>{{ formatCurrency(varianceData.currentSummary?.tax) }}</td><td>{{ formatCurrency(varianceData.compareSummary?.tax) }}</td><td :class="varianceClass(-varianceData.variance?.income_tax)">{{ formatCurrency(varianceData.variance?.income_tax) }}</td></tr>
                      <tr><td>Net</td><td>{{ formatCurrency(varianceData.currentSummary?.net) }}</td><td>{{ formatCurrency(varianceData.compareSummary?.net) }}</td><td :class="varianceClass(varianceData.variance?.net_salary)">{{ formatCurrency(varianceData.variance?.net_salary) }}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Email Sending Section -->
            <div class="card mb-4" v-if="periodDetails.status === 'Paid' || periodDetails.status === 'Approved'">
              <div class="card-header bg-info text-white">
                <h5 class="card-title mb-0">
                  <i class="mdi mdi-email-send me-2"></i>Email Payslips
                </h5>
              </div>
              <div class="card-body">
                <!-- Email Action Buttons -->
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <p class="text-muted mb-0">
                      Send payslips to {{ selectedTransactions.length > 0 ? selectedTransactions.length : 'all' }} employees
                    </p>
                  </div>
                  <div class="btn-group">
                    <button
                        @click="selectAllForEmail"
                        class="btn btn-outline-primary btn-sm"
                        :disabled="emailSending"
                    >
                      <i class="mdi mdi-checkbox-multiple-marked me-1"></i>
                      Select All
                    </button>
                    <button
                        @click="clearEmailSelection"
                        class="btn btn-outline-secondary btn-sm"
                        :disabled="emailSending"
                    >
                      <i class="mdi mdi-close me-1"></i>
                      Clear
                    </button>
                    <button
                        @click="sendPayslipsViaEmail"
                        class="btn btn-success btn-sm"
                        :disabled="selectedTransactions.length === 0 || emailSending"
                    >
                      <i class="mdi mdi-send me-1"></i>
                      Send Selected Payslips
                    </button>
                  </div>
                </div>

                <!-- Email Progress -->
                <div v-if="emailSending" class="email-progress-section">
                  <h6>Sending Payslips...</h6>
                  <div class="progress mb-3" style="height: 25px;">
                    <div
                        class="progress-bar progress-bar-striped progress-bar-animated"
                        role="progressbar"
                        :style="`width: ${emailProgress}%`"
                        :aria-valuenow="emailProgress"
                        aria-valuemin="0"
                        aria-valuemax="100"
                    >
                      {{ emailProgress }}%
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-4">
                      <div class="text-center">
                        <h4 class="text-success">{{ emailStats.sent }}</h4>
                        <p class="text-muted">Sent Successfully</p>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="text-center">
                        <h4 class="text-danger">{{ emailStats.failed }}</h4>
                        <p class="text-muted">Failed</p>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="text-center">
                        <h4 class="text-warning">{{ emailStats.pending }}</h4>
                        <p class="text-muted">Pending</p>
                      </div>
                    </div>
                  </div>
                  <div class="alert alert-info mt-3">
                    <i class="mdi mdi-information me-2"></i>
                    Currently processing: {{ currentEmployee.name || 'Preparing...' }}
                  </div>
                </div>

                <!-- Email Results -->
                <div v-if="emailResults.length > 0 && !emailSending" class="email-results mt-3">
                  <h6>Email Results</h6>
                  <div class="table-responsive">
                    <table class="table table-sm">
                      <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Email</th>
                        <th>Status</th>
                        <th>Message</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="result in emailResults" :key="result.employee_id"
                          :class="result.success ? 'table-success' : 'table-danger'">
                        <td>{{ result.employee_name }}</td>
                        <td>{{ result.email }}</td>
                        <td>
                            <span class="badge" :class="result.success ? 'bg-success' : 'bg-danger'">
                              {{ result.success ? 'Sent' : 'Failed' }}
                            </span>
                        </td>
                        <td>{{ result.message }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- Period summary and stats -->
            <div class="row mb-4">
              <div class="col-md-8">
                <div class="card h-100">
                  <div class="card-header">
                    <h5 class="card-title mb-0">Payroll Period Information</h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm table-borderless mb-0">
                        <tbody>
                        <tr>
                          <th style="width: 150px;">Period Name:</th>
                          <td>{{ periodDetails.period_name }}</td>
                        </tr>
                        <tr>
                          <th>Date Range:</th>
                          <td>{{ formatDate(periodDetails.start_date) }} to {{ formatDate(periodDetails.end_date) }}</td>
                        </tr>
                        <tr>
                          <th>Payment Date:</th>
                          <td>{{ formatDate(periodDetails.payment_date) }}</td>
                        </tr>
                        <tr>
                          <th>Created By:</th>
                          <td>{{ periodDetails.created_by }} ({{ formatDateTime(periodDetails.created_at) }})</td>
                        </tr>
                        <tr>
                          <th>Total Employees:</th>
                          <td>{{ periodDetails.employee_count || 0 }}</td>
                        </tr>
                        <tr>
                          <th>Notes:</th>
                          <td>{{ periodDetails.notes || 'No notes available' }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-4">
                <div class="card h-100">
                  <div class="card-header">
                    <h5 class="card-title mb-0">Summary</h5>
                  </div>
                  <div class="card-body">
                    <div class="d-flex justify-content-between mb-3">
                      <span class="text-muted">Gross Salary:</span>
                      <span class="fw-bold">{{ formatCurrency(periodDetails.total_gross) }}</span>
                    </div>
                    <div class="d-flex justify-content-between mb-3">
                      <span class="text-muted">Total Allowances:</span>
                      <span class="fw-bold">{{ formatCurrency(periodDetails.total_allowances) }}</span>
                    </div>
                    <div class="d-flex justify-content-between mb-3">
                      <span class="text-muted">Total Deductions:</span>
                      <span class="fw-bold text-danger">{{ formatCurrency(periodDetails.total_deductions) }}</span>
                    </div>
                    <div class="d-flex justify-content-between mb-3">
                      <span class="text-muted">Tax Amount:</span>
                      <span class="fw-bold text-danger">{{ formatCurrency(periodDetails.total_tax) }}</span>
                    </div>
                    <hr>
                    <div class="d-flex justify-content-between">
                      <span class="text-muted fw-bold">Net Payroll:</span>
                      <span class="fw-bold text-primary fs-5">{{ formatCurrency(periodDetails.total_net) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Export Section -->
            <div class="card mb-4">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="card-title mb-0">
                  <i class="mdi mdi-file-export me-2"></i>Export Options
                </h5>
                <button
                    @click="showExportBuilder = !showExportBuilder"
                    class="btn btn-primary btn-sm"
                >
                  <i class="mdi mdi-cog me-1"></i>
                  {{ showExportBuilder ? 'Hide' : 'Show' }} Custom Export
                </button>
              </div>
              <div class="card-body">
                <!-- Quick Export Buttons -->
                <div class="d-flex flex-wrap gap-2 mb-3">
                  <button
                      v-for="preset in exportPresets"
                      :key="preset.id"
                      @click="exportWithPreset(preset)"
                      class="btn"
                      :class="preset.id === 'bank_transfer' ? 'btn-success' : 'btn-outline-primary'"
                      :title="preset.description"
                  >
                    <i :class="preset.icon + ' me-1'"></i>
                    {{ preset.name }}
                  </button>
                </div>

                <!-- Custom Export Builder -->
                <div v-if="showExportBuilder" class="mt-3 pt-3 border-top">
                  <div class="row">
                    <!-- Column Selection -->
                    <div class="col-md-6">
                      <h6>Select Columns</h6>
                      <div class="d-flex justify-content-end mb-2">
                        <div class="btn-group btn-group-sm">
                          <button @click="selectAllColumns" class="btn btn-outline-success">
                            Select All
                          </button>
                          <button @click="clearAllColumns" class="btn btn-outline-danger">
                            Clear All
                          </button>
                        </div>
                      </div>
                      <div class="export-column-groups">
                        <div v-for="group in columnGroups" :key="group.id" class="mb-3">
                          <h6 class="text-muted">
                            <i :class="group.icon + ' me-1'"></i>
                            {{ group.name }}
                            <span class="badge bg-primary ms-2">
                              {{ getSelectedCountInGroup(group) }}/{{ group.columns.length }}
                            </span>
                          </h6>
                          <div class="ms-3">
                            <div v-for="col in group.columns" :key="col.key" class="form-check">
                              <input
                                  class="form-check-input"
                                  type="checkbox"
                                  :id="'col_' + col.key"
                                  v-model="selectedColumns[col.key]"
                              >
                              <label class="form-check-label" :for="'col_' + col.key">
                                {{ col.label }}
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Export Options -->
                    <div class="col-md-6">
                      <h6>Export Options</h6>
                      <div class="mb-3">
                        <label class="form-label">File Name</label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="exportOptions.fileName"
                        >
                      </div>
                      <div class="mb-3">
                        <label class="form-label">Format Options</label>
                        <div class="form-check">
                          <input
                              class="form-check-input"
                              type="checkbox"
                              id="includeHeaders"
                              v-model="exportOptions.includeHeaders"
                          >
                          <label class="form-check-label" for="includeHeaders">
                            Include Headers
                          </label>
                        </div>
                        <div class="form-check">
                          <input
                              class="form-check-input"
                              type="checkbox"
                              id="includeSummary"
                              v-model="exportOptions.includeSummary"
                          >
                          <label class="form-check-label" for="includeSummary">
                            Include Summary Row
                          </label>
                        </div>
                        <div class="form-check">
                          <input
                              class="form-check-input"
                              type="checkbox"
                              id="includeMetadata"
                              v-model="exportOptions.includeMetadata"
                          >
                          <label class="form-check-label" for="includeMetadata">
                            Include Metadata Header
                          </label>
                        </div>
                      </div>
                      <button
                          @click="exportCustomCSV"
                          class="btn btn-primary w-100"
                          :disabled="Object.values(selectedColumns).every(v => !v)"
                      >
                        <i class="mdi mdi-download me-1"></i>
                        Export Custom CSV
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Payroll Breakdown -->
            <div class="card mb-4">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="card-title mb-0">Payroll Breakdown</h5>
                <div class="input-group input-group-sm" style="width: 250px;">
                  <input
                      type="text"
                      class="form-control"
                      placeholder="Search employee"
                      v-model="employeeSearch"
                      @input="filterTransactions"
                  >
                  <button class="btn btn-primary" type="button">
                    <i class="mdi mdi-magnify"></i>
                  </button>
                </div>
              </div>
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table table-hover table-bordered table-sm">
                    <thead class="table-light">
                    <tr>
                      <th width="40">
                        <input
                            type="checkbox"
                            class="form-check-input"
                            @change="toggleAllSelection"
                            :checked="areAllSelected"
                            :disabled="emailSending"
                        >
                      </th>
                      <th>Employee</th>
                      <th>Department</th>
                      <th>Email</th>
                      <th>Basic Salary</th>
                      <th>Allowances</th>
                      <th>Deductions</th>
                      <th>Tax</th>
                      <th>Net Salary</th>
                      <th>Status</th>
                      <th class="text-center">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="loading || paginatedTransactions.length === 0">
                      <td colspan="11" class="text-center py-3">
                        <div v-if="loading" class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                        <span v-if="loading">Loading transactions...</span>
                        <span v-else>No transactions found</span>
                      </td>
                    </tr>
                    <tr v-for="transaction in paginatedTransactions" :key="transaction.id">
                      <td>
                        <input
                            type="checkbox"
                            class="form-check-input"
                            :value="transaction.id"
                            v-model="selectedTransactions"
                            :disabled="emailSending"
                        >
                      </td>
                      <td>
                        <div class="d-flex align-items-center">
                          <div class="avatar-xs me-2 bg-light rounded-circle p-1 text-center">
                            <i class="mdi mdi-account font-size-18 text-primary"></i>
                          </div>
                          <div>
                            <h6 class="font-size-14 mb-0">{{ transaction.employee_name }}</h6>
                            <small class="text-muted">{{ transaction.empNo }}</small>
                          </div>
                        </div>
                      </td>
                      <td>{{ transaction.department }}</td>
                      <td>
                        <small>{{ transaction.email || 'No email' }}</small>
                        <i v-if="!transaction.email" class="mdi mdi-alert text-warning ms-1" title="No email address"></i>
                      </td>
                      <td>{{ formatCurrency(transaction.basic_salary) }}</td>
                      <td class="text-success">{{ formatCurrency(transaction.total_allowances) }}</td>
                      <td class="text-danger">{{ formatCurrency(transaction.total_deductions) }}</td>
                      <td class="text-danger">{{ formatCurrency(transaction.income_tax) }}</td>
                      <td class="fw-bold">{{ formatCurrency(transaction.net_salary) }}</td>
                      <td>
                          <span class="badge" :class="getPaymentStatusBadgeClass(transaction.payment_status)">
                            {{ transaction.payment_status }}
                          </span>
                      </td>
                      <td class="text-center">
                        <button
                            @click="viewTransactionDetails(transaction)"
                            class="btn btn-sm btn-info me-1"
                            title="View Details"
                        >
                          <i class="mdi mdi-eye"></i>
                        </button>
                        <button
                            @click="generatePayslip(transaction)"
                            class="btn btn-sm btn-secondary me-1"
                            title="Generate Payslip"
                        >
                          <i class="mdi mdi-file-document"></i>
                        </button>
                        <button
                            @click="sendSinglePayslip(transaction)"
                            class="btn btn-sm btn-success"
                            title="Email Payslip"
                            :disabled="!transaction.email || emailSending"
                        >
                          <i class="mdi mdi-email"></i>
                        </button>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Pagination -->
                <div v-if="filteredTransactions.length > pageSize" class="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    Showing <span class="fw-medium">{{ paginatedTransactions.length }}</span> of
                    <span class="fw-medium">{{ filteredTransactions.length }}</span> transactions
                  </div>
                  <nav>
                    <ul class="pagination pagination-sm mb-0">
                      <li class="page-item" :class="{ disabled: currentPage <= 1 }">
                        <a class="page-link" href="javascript:void(0)" @click="changePage(currentPage - 1)">
                          <i class="mdi mdi-chevron-left"></i>
                        </a>
                      </li>
                      <li
                          v-for="page in displayedPages"
                          :key="page"
                          class="page-item"
                          :class="{ active: page === currentPage }"
                      >
                        <a class="page-link" href="javascript:void(0)" @click="changePage(page)">
                          {{ page }}
                        </a>
                      </li>
                      <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
                        <a class="page-link" href="javascript:void(0)" @click="changePage(currentPage + 1)">
                          <i class="mdi mdi-chevron-right"></i>
                        </a>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>

            <!-- Activity History -->
            <div class="card" v-if="periodDetails.activities && periodDetails.activities.length > 0">
              <div class="card-header">
                <h5 class="card-title mb-0">Activity History</h5>
              </div>
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table table-sm table-hover">
                    <thead class="table-light">
                    <tr>
                      <th>Date & Time</th>
                      <th>Activity</th>
                      <th>User</th>
                      <th>Details</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="activity in periodDetails.activities" :key="activity.id">
                      <td>{{ formatDateTime(activity.performed_at) }}</td>
                      <td>
                          <span
                              class="badge rounded-pill"
                              :class="getActivityTypeClass(activity.action_type)"
                          >
                            {{ formatActivityType(activity.action_type) }}
                          </span>
                      </td>
                      <td>{{ activity.performed_by }}</td>
                      <td>{{ activity.new_value || 'No details' }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="closeModalFromEvent($event)">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { reactive, ref } from 'vue';
import { authUrl, execute } from "@/api";

export default {
  name: 'PayrollDetails',
  props: {
    periodId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      periodDetails: null,
      transactions: [],
      filteredTransactions: [],
      employeeSearch: '',
      currentPage: 1,
      pageSize: 10,
      modal: null,
      showExportBuilder: false,

      // Email sending related
      selectedTransactions: [],
      emailSending: false,
      emailProgress: 0,
      emailStats: {
        sent: 0,
        failed: 0,
        pending: 0
      },
      currentEmployee: {},
      emailResults: [],
      emailBatchSize: 5, // Send 5 emails at a time
      emailDelay: 2000, // 2 second delay between batches

      // Export related data - Using empNo as primary identifier
      exportPresets: [
        {
          id: 'basic',
          name: 'Basic Summary',
          icon: 'mdi mdi-file-document-outline',
          description: 'Employee info with salary totals',
          columns: ['empNo', 'employee_name', 'department', 'basic_salary', 'net_salary']
        },
        {
          id: 'detailed',
          name: 'Detailed Report',
          icon: 'mdi mdi-file-document-multiple',
          description: 'All salary components and calculations',
          columns: ['empNo', 'employee_name', 'department', 'basic_salary', 'gross_salary', 'total_allowances', 'total_deductions', 'taxable_income', 'income_tax', 'net_salary', 'payment_status']
        },
        {
          id: 'bank_transfer',
          name: 'Bank Transfer (Salary Sheet)',
          icon: 'mdi mdi-bank-transfer-out',
          description: 'Bank upload format for salary transfers',
          columns: ['Bank Code', 'Branch Code', 'Account Number', 'Account Name', 'Amount', 'Transaction Type'],
          isSpecialFormat: true
        },
        {
          id: 'statutory',
          name: 'Statutory',
          icon: 'mdi mdi-bank',
          description: 'NSSF, SHIF, and tax details',
          columns: ['empNo', 'employee_name', 'kra_pin', 'nssf_number', 'nssf_amount', 'shif_number', 'shif_amount', 'income_tax', 'net_salary']
        },
        {
          id: 'payment',
          name: 'Payment Schedule',
          icon: 'mdi mdi-cash-multiple',
          description: 'Payment information with bank details',
          columns: ['empNo', 'employee_name', 'net_salary', 'bank_name', 'bank_code', 'branch_code', 'account_number', 'payment_status', 'payment_reference']
        },
        {
          id: 'nssf',
          name: 'NSSF Template',
          icon: 'mdi mdi-file-chart',
          description: 'NSSF submission format',
          columns: ['empNo', 'employee_name', 'nationalId', 'nssf_number', 'basic_salary', 'nssf_amount']
        },
        {
          id: 'shif',
          name: 'SHIF Template',
          icon: 'mdi mdi-hospital-building',
          description: 'SHIF submission format',
          columns: ['empNo', 'employee_name', 'nationalId', 'shif_number', 'basic_salary', 'shif_amount']
        },
        {
          id: 'kra',
          name: 'KRA P9 Form',
          icon: 'mdi mdi-receipt',
          description: 'KRA tax submission format',
          columns: ['empNo', 'employee_name', 'kra_pin', 'basic_salary', 'taxable_income', 'income_tax', 'personal_relief', 'net_paye']
        },
        {
          id: 'hr',
          name: 'HR Summary',
          icon: 'mdi mdi-account-group',
          description: 'HR management report',
          columns: ['empNo', 'employee_name', 'nationalId', 'department', 'employee_status', 'basic_salary', 'net_salary']
        }
      ],

      columnGroups: [
        {
          id: 'employee',
          name: 'Employee Info',
          icon: 'mdi mdi-account',
          columns: [
            { key: 'empNo', label: 'Employee Number', type: 'text' },
            { key: 'employee_name', label: 'Employee Name', type: 'text' },
            { key: 'nationalId', label: 'National ID', type: 'text' },
            { key: 'department', label: 'Department', type: 'text' },
            { key: 'employee_status', label: 'Status', type: 'text' },
            { key: 'employee_id', label: 'System ID', type: 'text' }
          ]
        },
        {
          id: 'salary',
          name: 'Salary Components',
          icon: 'mdi mdi-cash',
          columns: [
            { key: 'basic_salary', label: 'Basic Salary', type: 'currency' },
            { key: 'gross_salary', label: 'Gross Salary', type: 'currency' },
            { key: 'total_allowances', label: 'Total Allowances', type: 'currency' },
            { key: 'total_deductions', label: 'Total Deductions', type: 'currency' },
            { key: 'taxable_income', label: 'Taxable Income', type: 'currency' },
            { key: 'income_tax', label: 'Income Tax (PAYE)', type: 'currency' },
            { key: 'net_salary', label: 'Net Salary', type: 'currency' }
          ]
        },
        {
          id: 'statutory',
          name: 'Statutory Deductions',
          icon: 'mdi mdi-bank',
          columns: [
            { key: 'kra_pin', label: 'KRA PIN', type: 'text' },
            { key: 'nssf_number', label: 'NSSF Number', type: 'text' },
            { key: 'nssf_amount', label: 'NSSF Amount', type: 'currency' },
            { key: 'shif_number', label: 'SHIF Number', type: 'text' },
            { key: 'shif_amount', label: 'SHIF Amount', type: 'currency' },
            { key: 'nhif_number', label: 'NHIF Number (Legacy)', type: 'text' },
            { key: 'housing_levy', label: 'Housing Levy', type: 'currency' },
            { key: 'personal_relief', label: 'Personal Relief', type: 'currency' },
            { key: 'net_paye', label: 'Net PAYE', type: 'currency' }
          ]
        },
        {
          id: 'payment',
          name: 'Payment Information',
          icon: 'mdi mdi-bank-transfer',
          columns: [
            { key: 'payment_status', label: 'Payment Status', type: 'text' },
            { key: 'payment_date', label: 'Payment Date', type: 'date' },
            { key: 'payment_reference', label: 'Payment Reference', type: 'text' },
            { key: 'payment_method', label: 'Payment Method', type: 'text' },
            { key: 'bank_name', label: 'Bank Name', type: 'text' },
            { key: 'bank_code', label: 'Bank Code', type: 'text' },
            { key: 'account_number', label: 'Account Number', type: 'text' },
            { key: 'branch_code', label: 'Branch Code', type: 'text' }
          ]
        }
      ],

      selectedColumns: reactive({}),
      exportOptions: reactive({
        fileName: 'payroll_export',
        includeHeaders: true,
        includeSummary: false,
        includeMetadata: false
      }),

      comparePeriodId: '',
      varianceData: null,
      ippdExporting: false,
      ippdImportFile: null,
      otherPeriodsForCompare: []
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.filteredTransactions.length / this.pageSize);
    },
    paginatedTransactions() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredTransactions.slice(start, end);
    },
    displayedPages() {
      const pages = [];
      const maxPages = 5;
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(this.totalPages, start + maxPages - 1);

      if (end - start < maxPages - 1) {
        start = Math.max(1, end - maxPages + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    areAllSelected() {
      return this.paginatedTransactions.length > 0 &&
          this.paginatedTransactions.every(t => this.selectedTransactions.includes(t.id));
    }
  },

  watch: {
    periodId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchPayrollDetails(newVal);
        }
      }
    }
  },

  mounted() {
    this.initModal();
    this.initializeDefaultColumns();
  },

  methods: {
    initModal() {
      this.$nextTick(() => {
        // Check if Bootstrap is available
        if (typeof bootstrap !== 'undefined') {
          this.modal = new bootstrap.Modal(document.getElementById('payrollDetailsModal'));
          document.getElementById('payrollDetailsModal').addEventListener('hidden.bs.modal', this.resetData);
        }
      });
    },

    initializeDefaultColumns() {
      // Initialize with basic columns selected
      const basicPreset = this.exportPresets.find(p => p.id === 'basic');
      if (basicPreset) {
        // Create new object with selected columns
        const newSelection = {};
        basicPreset.columns.forEach(col => {
          newSelection[col] = true;
        });
        // Replace entire object for Vue 3 reactivity
        Object.assign(this.selectedColumns, newSelection);
      }
    },

    showModal(periodId) {
      if (periodId) {
        this.fetchPayrollDetails(periodId);
      }

      if (this.modal) {
        this.modal.show();
      } else {
        this.initModal();
        setTimeout(() => {
          if (this.modal) this.modal.show();
        }, 100);
      }
    },

    hideModal() {
      if (this.modal) {
        this.modal.hide();
      } else {
        const el = document.getElementById('payrollDetailsModal');
        if (el && typeof bootstrap !== 'undefined') {
          const instance = bootstrap.Modal.getInstance(el);
          if (instance) instance.hide();
        }
      }
    },
    closeModalFromEvent() {
      this.hideModal();
    },

    resetData() {
      this.employeeSearch = '';
      this.currentPage = 1;
      this.showExportBuilder = false;
      this.selectedTransactions = [];
      this.emailResults = [];
      this.varianceData = null;
      this.comparePeriodId = '';
      this.filterTransactions();
    },

    async loadOtherPeriodsForCompare() {
      try {
        const data = new FormData();
        data.append('function', 'getPayrollPeriods');
        data.append('limit', '50');
        const res = await execute(data, authUrl);
        if (res?.data?.success && res.data.data?.payrollPeriods) {
          this.otherPeriodsForCompare = (res.data.data.payrollPeriods || []).filter(p => String(p.id) !== String(this.periodId));
        }
      } catch (e) {
        this.otherPeriodsForCompare = [];
      }
    },

    async loadVarianceAnalysis() {
      if (!this.periodId) return;
      try {
        const data = new FormData();
        data.append('function', 'getPayrollVarianceAnalysis');
        data.append('periodId', this.periodId);
        if (this.comparePeriodId) data.append('comparePeriodId', this.comparePeriodId);
        const res = await execute(data, authUrl);
        if (res?.data?.success && res.data.data) {
          this.varianceData = res.data.data;
        } else {
          this.varianceData = null;
        }
      } catch (e) {
        this.varianceData = null;
      }
    },

    varianceClass(val) {
      if (val == null || val === '') return '';
      const n = parseFloat(val);
      if (n > 0) return 'text-success';
      if (n < 0) return 'text-danger';
      return '';
    },

    async exportIPPD() {
      if (!this.periodId) return;
      this.ippdExporting = true;
      try {
        const data = new FormData();
        data.append('function', 'exportPayrollIPPD');
        data.append('periodId', this.periodId);
        const res = await execute(data, authUrl);
        if (res?.data?.success && res.data.data?.filename) {
          const base = window.location.origin + (import.meta.env?.BASE_URL || '/');
          const authBase = base.replace(/\/$/, '').replace(/\/[^/]+$/, '') || (authUrl && authUrl.replace(/index\.php.*$/, ''));
          const url = (authUrl && authUrl.replace(/index\.php.*$/, '') || base) + 'exports/' + res.data.data.filename;
          window.open(url, '_blank');
          this.$toast?.success('IPPD export ready: ' + res.data.data.filename);
        } else {
          this.$toast?.error(res?.data?.message || 'Export failed');
        }
      } catch (e) {
        this.$toast?.error(e?.message || 'Export failed');
      } finally {
        this.ippdExporting = false;
      }
    },

    onIppdFileSelect(e) {
      this.ippdImportFile = e.target.files?.[0] || null;
    },

    async importIPPD() {
      if (!this.ippdImportFile) return;
      try {
        const data = new FormData();
        data.append('function', 'importPayrollIPPD');
        data.append('file', this.ippdImportFile);
        if (this.periodId) data.append('periodId', this.periodId);
        data.append('dryRun', '1');
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.$toast?.success(res.data.message || 'IPPD file validated');
        } else {
          this.$toast?.error(res?.data?.message || 'Validation failed');
        }
      } catch (e) {
        this.$toast?.error(e?.message || 'Import failed');
      }
    },

    fetchPayrollDetails(periodId) {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getPayrollPeriodDetails");
      data.append("periodId", periodId);

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.periodDetails = res.data.data.periodDetails;
              // Add company info if available
              if (res.data.data.companyInfo) {
                this.periodDetails.companyName = res.data.data.companyInfo.companyName;
                this.periodDetails.companyAddress = res.data.data.companyInfo.companyAddress;
              }
              this.transactions = this.processTransactions(res.data.data.transactions || []);
              this.filteredTransactions = [...this.transactions];
              this.loadOtherPeriodsForCompare();
              this.loadVarianceAnalysis();
            } else {
              console.error("Error fetching payroll details:", res.data.message);
              alert(res.data.message || "Failed to load payroll details");
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
            alert("An error occurred while loading payroll details");
          });
    },

    processTransactions(transactions) {
      // Process transactions to extract component values
      return transactions.map(transaction => {
        const processed = { ...transaction };

        // Extract email if available
        processed.email = transaction.email || transaction.employee_email || '';

        // Extract government IDs
        if (transaction.government_id_lookup) {
          processed.kra_pin = transaction.government_id_lookup.KRA_PIN || '';
          processed.nssf_number = transaction.government_id_lookup.NSSF || '';
          processed.shif_number = transaction.government_id_lookup.SHIF || '';
          processed.nhif_number = transaction.government_id_lookup.NHIF || '';
        }

        // Extract component amounts
        if (transaction.components) {
          // NSSF Amount
          const nssf = transaction.components.Deduction?.find(d =>
              d.component_name.includes('NSSF'));
          processed.nssf_amount = nssf?.amount || 0;

          // SHIF Amount
          const shif = transaction.components.Deduction?.find(d =>
              d.component_name === 'SHIF');
          processed.shif_amount = shif?.amount || 0;

          // Housing Levy
          const housing = transaction.components.Deduction?.find(d =>
              d.component_name.includes('Housing'));
          processed.housing_levy = housing?.amount || 0;

          // Personal Relief
          const relief = transaction.components.Relief?.find(r =>
              r.component_name === 'Personal Relief');
          processed.personal_relief = Math.abs(parseFloat(relief?.amount || 0));

          // Net PAYE
          const netPaye = transaction.components.Tax?.find(t =>
              t.component_name === 'Net PAYE');
          processed.net_paye = netPaye?.amount || transaction.income_tax || 0;
        }

        // Ensure empNo is available
        processed.empNo = transaction.empNo || transaction.employee_number || '';
        processed.employee_status = transaction.employee_status || 'Active';

        return processed;
      });
    },

    filterTransactions() {
      if (!this.employeeSearch.trim()) {
        this.filteredTransactions = [...this.transactions];
      } else {
        const search = this.employeeSearch.toLowerCase();
        this.filteredTransactions = this.transactions.filter(transaction =>
            transaction.employee_name.toLowerCase().includes(search) ||
            transaction.empNo.toLowerCase().includes(search) ||
            (transaction.employee_id && transaction.employee_id.toLowerCase().includes(search)) ||
            (transaction.department && transaction.department.toLowerCase().includes(search))
        );
      }
      this.currentPage = 1;
    },

    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },

    // Email Selection Methods
    toggleAllSelection(event) {
      if (event.target.checked) {
        this.selectedTransactions = this.paginatedTransactions.map(t => t.id);
      } else {
        this.selectedTransactions = [];
      }
    },

    selectAllForEmail() {
      this.selectedTransactions = this.filteredTransactions.map(t => t.id);
    },

    clearEmailSelection() {
      this.selectedTransactions = [];
    },

    // Email Sending Methods
    async sendPayslipsViaEmail() {
      if (this.selectedTransactions.length === 0) {
        alert('Please select at least one employee to send payslips');
        return;
      }

      const confirmed = confirm(`Are you sure you want to send payslips to ${this.selectedTransactions.length} employees?`);
      if (!confirmed) return;

      // Get selected transactions
      const selectedEmployees = this.transactions.filter(t =>
          this.selectedTransactions.includes(t.id)
      );

      // Filter out employees without email
      const employeesWithEmail = selectedEmployees.filter(t => t.email);
      const employeesWithoutEmail = selectedEmployees.filter(t => !t.email);

      if (employeesWithoutEmail.length > 0) {
        alert(`${employeesWithoutEmail.length} employees don't have email addresses. They will be skipped.`);
      }

      if (employeesWithEmail.length === 0) {
        alert('None of the selected employees have email addresses');
        return;
      }

      // Initialize email sending
      this.emailSending = true;
      this.emailProgress = 0;
      this.emailResults = [];
      this.emailStats = {
        sent: 0,
        failed: 0,
        pending: employeesWithEmail.length
      };

      // Process in batches
      await this.processBatchEmails(employeesWithEmail);

      // Complete
      this.emailSending = false;
      this.showEmailSummary();
    },

    async processBatchEmails(employees) {
      const batches = [];

      // Create batches
      for (let i = 0; i < employees.length; i += this.emailBatchSize) {
        batches.push(employees.slice(i, i + this.emailBatchSize));
      }

      // Process each batch
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];

        // Process emails in current batch
        const batchPromises = batch.map(employee => this.sendPayslipEmail(employee));
        await Promise.all(batchPromises);

        // Update progress
        this.emailProgress = Math.round(((batchIndex + 1) / batches.length) * 100);

        // Delay before next batch (except for last batch)
        if (batchIndex < batches.length - 1) {
          await this.delay(this.emailDelay);
        }
      }
    },

    async sendPayslipEmail(employee) {
      this.currentEmployee = {
        name: employee.employee_name,
        email: employee.email
      };

      const data = new FormData();
      data.append("function", "sendPayslipEmail");
      data.append("transaction_id", employee.id);
      data.append("employee_id", employee.employee_id);
      data.append("period_id", this.periodDetails.id);
      data.append("email", employee.email);
      data.append("employee_name", employee.employee_name);
      data.append("empNo", employee.empNo);

      try {
        const response = await execute(data, authUrl);

        if (response.data.success) {
          this.emailStats.sent++;
          this.emailResults.push({
            employee_id: employee.employee_id,
            employee_name: employee.employee_name,
            email: employee.email,
            success: true,
            message: 'Payslip sent successfully'
          });
        } else {
          this.emailStats.failed++;
          this.emailResults.push({
            employee_id: employee.employee_id,
            employee_name: employee.employee_name,
            email: employee.email,
            success: false,
            message: response.data.message || 'Failed to send email'
          });
        }
      } catch (error) {
        this.emailStats.failed++;
        this.emailResults.push({
          employee_id: employee.employee_id,
          employee_name: employee.employee_name,
          email: employee.email,
          success: false,
          message: 'Network error or server unavailable'
        });
      }

      this.emailStats.pending--;
    },

    async sendSinglePayslip(transaction) {
      if (!transaction.email) {
        alert('This employee does not have an email address');
        return;
      }

      const confirmed = confirm(`Send payslip to ${transaction.employee_name} (${transaction.email})?`);
      if (!confirmed) return;

      this.emailSending = true;
      this.emailResults = [];

      await this.sendPayslipEmail(transaction);

      this.emailSending = false;

      if (this.emailResults.length > 0 && this.emailResults[0].success) {
        alert('Payslip sent successfully!');
      } else {
        alert('Failed to send payslip: ' + (this.emailResults[0]?.message || 'Unknown error'));
      }
    },

    showEmailSummary() {
      const summary = `
Email Sending Complete!
-----------------------
Successfully Sent: ${this.emailStats.sent}
Failed: ${this.emailStats.failed}
Total Processed: ${this.emailStats.sent + this.emailStats.failed}
      `;
      alert(summary);
    },

    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    // Export Methods
    selectAllColumns() {
      // Create new object with all columns selected
      const newSelection = {};
      this.columnGroups.forEach(group => {
        group.columns.forEach(col => {
          newSelection[col.key] = true;
        });
      });
      // Replace entire object for Vue 3 reactivity
      Object.keys(this.selectedColumns).forEach(key => delete this.selectedColumns[key]);
      Object.assign(this.selectedColumns, newSelection);
    },

    clearAllColumns() {
      // Clear all selections
      Object.keys(this.selectedColumns).forEach(key => {
        delete this.selectedColumns[key];
      });
    },

    getSelectedCountInGroup(group) {
      return group.columns.filter(col => this.selectedColumns[col.key]).length;
    },

    exportWithPreset(preset) {
      // Clear existing selections
      Object.keys(this.selectedColumns).forEach(key => {
        delete this.selectedColumns[key];
      });

      // Add preset columns
      const newSelection = {};
      preset.columns.forEach(col => {
        newSelection[col] = true;
      });
      Object.assign(this.selectedColumns, newSelection);

      // Export with preset name
      this.exportCSV(preset.id);
    },

    exportCustomCSV() {
      this.exportCSV('custom');
    },

    exportCSV(type) {
      // Special handling for bank transfer format
      if (type === 'bank_transfer') {
        this.exportBankTransferFormat();
        return;
      }

      const selectedCols = Object.keys(this.selectedColumns).filter(key => this.selectedColumns[key]);

      if (selectedCols.length === 0) {
        alert('Please select at least one column to export');
        return;
      }

      let csvContent = '';

      // Add metadata if requested
      if (this.exportOptions.includeMetadata && this.periodDetails) {
        csvContent += `Company: ${this.periodDetails.companyName || 'N/A'}\n`;
        csvContent += `Period: ${this.periodDetails.period_name}\n`;
        csvContent += `Date Range: ${this.formatDate(this.periodDetails.start_date)} to ${this.formatDate(this.periodDetails.end_date)}\n`;
        csvContent += `Payment Date: ${this.formatDate(this.periodDetails.payment_date)}\n`;
        csvContent += `Total Employees: ${this.periodDetails.employee_count}\n`;
        csvContent += `Total Net Payroll: ${this.formatCurrency(this.periodDetails.total_net).replace('KES', '').trim()}\n`;
        csvContent += `Export Date: ${new Date().toLocaleString()}\n`;
        csvContent += `Exported By: ${this.periodDetails.created_by || 'System'}\n\n`;
      }

      // Add headers
      if (this.exportOptions.includeHeaders) {
        const headers = selectedCols.map(key => {
          const col = this.findColumn(key);
          return col ? col.label : key;
        });
        csvContent += headers.join(',') + '\n';
      }

      // Add data rows
      this.filteredTransactions.forEach(transaction => {
        const row = selectedCols.map(key => {
          const col = this.findColumn(key);
          const value = transaction[key] || '';
          return this.formatCSVCell(value, col ? col.type : 'text');
        });
        csvContent += row.join(',') + '\n';
      });

      // Add summary row if requested
      if (this.exportOptions.includeSummary) {
        csvContent += '\n';
        const summaryRow = selectedCols.map(key => {
          const col = this.findColumn(key);
          if (col && col.type === 'currency') {
            const sum = this.filteredTransactions.reduce((acc, t) =>
                acc + parseFloat(t[key] || 0), 0);
            return this.formatCSVCell(sum, 'currency');
          } else if (key === 'employee_name') {
            return '"TOTAL"';
          } else if (key === 'empNo') {
            return '"SUMMARY"';
          }
          return '""';
        });
        csvContent += summaryRow.join(',');
      }

      // Download the file
      this.downloadCSV(csvContent, `${this.exportOptions.fileName}_${type}`);
    },

    exportBankTransferFormat() {
      // Create CSV in exact bank format with standard column names
      let csvContent = '';

      // Headers with standard naming convention
      const headers = [
        'Bank Code',
        'Branch Code',
        'Account Number',
        'Account Name',
        'Amount',
        'Transaction Type',
        'Bank Name',
        'Employee Number',
        'National ID',
        'Department',
        'Payment Reference',
        'Payment Date'
      ];
      csvContent += headers.join(',') + '\n';

      // Add data rows - only include employees with valid bank details
      this.filteredTransactions.forEach(transaction => {
        // Only include if has bank details and is paid/pending
        if (transaction.bank_code && transaction.account_number &&
            (transaction.payment_status === 'Paid' || transaction.payment_status === 'Pending')) {

          const row = [
            transaction.bank_code || '',                    // Bank Code
            transaction.branch_code || '',                  // Branch Code
            transaction.account_number || '',               // Account Number
            transaction.employee_name || '',                // Account Name
            parseFloat(transaction.net_salary || 0).toFixed(2), // Amount
            'SALARY',                                       // Transaction Type
            transaction.bank_name || '',                    // Bank Name
            transaction.empNo || '',                        // Employee Number
            transaction.nationalId || '',                   // National ID
            transaction.department || '',                   // Department
            transaction.payment_reference || '',            // Payment Reference
            transaction.payment_date || this.formatDate(this.periodDetails.payment_date) // Payment Date
          ];

          // Format row for CSV
          const formattedRow = row.map(value => {
            const strValue = String(value);
            // Only quote if contains special characters
            if (strValue.includes(',') || strValue.includes('"') || strValue.includes('\n')) {
              return `"${strValue.replace(/"/g, '""')}"`;
            }
            return strValue;
          });

          csvContent += formattedRow.join(',') + '\n';
        }
      });

      // Download with specific filename for bank
      const periodName = this.periodDetails.period_name.replace(/\s+/g, '_');
      const dateStr = new Date().toISOString().split('T')[0];
      const fileName = `Salary_Sheet_${periodName}_${dateStr}`;

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `${fileName}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      const validCount = this.filteredTransactions.filter(t =>
          t.bank_code && t.account_number &&
          (t.payment_status === 'Paid' || t.payment_status === 'Pending')
      ).length;

      const message = `Successfully exported ${validCount} salary transfers to Bank Transfer format`;
      if (this.$toast && this.$toast.success) {
        this.$toast.success(message);
      } else {
        alert(message);
      }
    },

    findColumn(key) {
      for (const group of this.columnGroups) {
        const col = group.columns.find(c => c.key === key);
        if (col) return col;
      }
      return null;
    },

    formatCSVCell(value, type) {
      if (value === null || value === undefined || value === '') {
        return '""';
      }

      let formatted = value;

      switch (type) {
        case 'currency':
          formatted = parseFloat(value || 0).toFixed(2);
          break;
        case 'date':
          if (value) {
            const date = new Date(value);
            formatted = date.toISOString().split('T')[0];
          }
          break;
        default:
          formatted = String(value);
      }

      // Escape CSV special characters
      if (formatted.includes(',') || formatted.includes('"') || formatted.includes('\n')) {
        formatted = `"${formatted.replace(/"/g, '""')}"`;
      }

      return formatted;
    },

    downloadCSV(content, fileName) {
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const periodName = this.periodDetails.period_name.replace(/\s+/g, '_');
      const dateStr = new Date().toISOString().split('T')[0];
      const fullFileName = `${fileName}_${periodName}_${dateStr}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', fullFileName);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      const message = `Successfully exported ${this.filteredTransactions.length} transactions to CSV`;
      if (this.$toast && this.$toast.success) {
        this.$toast.success(message);
      } else {
        alert(message);
      }
    },

    // UI Helper Methods
    formatCurrency(value) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value || 0);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      return new Date(dateTimeString).toLocaleString('en-US', options);
    },

    getStatusBadgeClass(status) {
      const statusClasses = {
        'Draft': 'bg-secondary',
        'Processing': 'bg-info',
        'Processed': 'bg-primary',
        'Locked': 'bg-warning',
        'Approved': 'bg-success',
        'Paid': 'bg-success',
        'Closed': 'bg-dark',
        'Rejected': 'bg-danger',
        'Pending': 'bg-warning'
      };
      return statusClasses[status] || 'bg-secondary';
    },

    getPaymentStatusBadgeClass(status) {
      const statusClasses = {
        'Pending': 'bg-warning',
        'Processing': 'bg-info',
        'Paid': 'bg-success',
        'Failed': 'bg-danger',
        'Cancelled': 'bg-dark'
      };
      return statusClasses[status] || 'bg-secondary';
    },

    getActivityTypeClass(activityType) {
      if (!activityType) return 'bg-secondary';
      const typeClasses = {
        'create': 'bg-primary',
        'update': 'bg-info',
        'process': 'bg-warning',
        'approve': 'bg-success',
        'reject': 'bg-danger',
        'payment': 'bg-purple',
        'export': 'bg-secondary'
      };
      return typeClasses[activityType.toLowerCase()] || 'bg-secondary';
    },

    formatActivityType(activityType) {
      if (!activityType) return 'Unknown';
      return activityType.charAt(0).toUpperCase() + activityType.slice(1);
    },

    // Period Actions
    canProcessPeriod(periodDetails) {
      return periodDetails.status === 'Draft';
    },

    canLockPeriod(periodDetails) {
      return periodDetails.status === 'Processed';
    },

    canApprovePeriod(periodDetails) {
      return periodDetails.status === 'Locked' || periodDetails.status === 'Processed';
    },

    canMarkPaidPeriod(periodDetails) {
      return periodDetails.status === 'Approved';
    },

    canClosePeriod(periodDetails) {
      return periodDetails.status === 'Paid';
    },

    async lockPeriod() {
      if (!this.periodDetails) return;
      try {
        const data = new FormData();
        data.append('function', 'lockPayroll');
        data.append('periodId', this.periodId);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.$toast?.success(res.data.message || 'Payroll locked');
          this.fetchPayrollDetails(this.periodId);
          this.$emit('period-updated');
        } else {
          this.$toast?.error(res?.data?.message || 'Failed to lock');
        }
      } catch (e) {
        this.$toast?.error(e?.message || 'Failed to lock payroll');
      }
    },

    async markAsPaid() {
      if (!this.periodDetails) return;
      const ref = window.prompt('Payment reference (optional):');
      if (ref === null) return;
      try {
        const data = new FormData();
        data.append('function', 'markAsPaid');
        data.append('periodId', this.periodId);
        data.append('paymentDate', new Date().toISOString().slice(0, 10));
        data.append('paymentReference', ref || '');
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.$toast?.success('Marked as paid');
          this.fetchPayrollDetails(this.periodId);
          this.$emit('period-updated');
        } else {
          this.$toast?.error(res?.data?.message || 'Failed');
        }
      } catch (e) {
        this.$toast?.error(e?.message || 'Failed');
      }
    },

    processPeriod() {
      if (!this.periodDetails) return;

      if (!confirm(`Are you sure you want to process payroll for period "${this.periodDetails.period_name}"?`)) {
        return;
      }

      this.loading = true;

      const data = new FormData();
      data.append("function", "processPeriod");
      data.append("periodId", this.periodDetails.id);

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              alert(res.data.message || "Payroll processed successfully");
              this.fetchPayrollDetails(this.periodDetails.id);
            } else {
              alert(res.data.message || "Failed to process payroll");
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
            alert("An error occurred while processing the payroll");
          });
    },

    approvePeriod() {
      if (!this.periodDetails) return;

      const comments = prompt("Enter approval comments (optional):");

      this.loading = true;

      const data = new FormData();
      data.append("function", "approvePeriod");
      data.append("periodId", this.periodDetails.id);

      if (comments) {
        data.append("comments", comments);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              alert(res.data.message || "Payroll approved successfully");
              this.fetchPayrollDetails(this.periodDetails.id);
            } else {
              alert(res.data.message || "Failed to approve payroll");
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
            alert("An error occurred while approving the payroll");
          });
    },

    closePeriod() {
      if (!this.periodDetails) return;

      if (!confirm(`Are you sure you want to close payroll period "${this.periodDetails.period_name}"?`)) {
        return;
      }

      this.loading = true;

      const data = new FormData();
      data.append("function", "closePeriod");
      data.append("periodId", this.periodDetails.id);

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              alert(res.data.message || "Payroll period closed successfully");
              this.fetchPayrollDetails(this.periodDetails.id);
            } else {
              alert(res.data.message || "Failed to close payroll period");
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
            alert("An error occurred while closing the payroll period");
          });
    },

    viewTransactionDetails(transaction) {
      this.$emit('view-transaction', transaction.id);
    },

    generatePayslip(transaction) {
      this.$emit('view-payslip', transaction.id);
    }
  }
};
</script>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}

.export-column-groups {
  max-height: 400px;
  overflow-y: auto;
  padding: 10px;
  border: 1px solid #dee2e6;
  border-radius: 5px;
}

.form-check {
  margin-bottom: 0.5rem;
}

.badge {
  font-size: 0.75rem;
}

.table-responsive {
  max-height: 500px;
  overflow: auto;
}

.avatar-xs {
  width: 32px;
  height: 32px;
}

.font-size-48 {
  font-size: 48px;
}

.font-size-18 {
  font-size: 18px;
}

.font-size-14 {
  font-size: 14px;
}

.email-progress-section {
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-top: 20px;
}

.email-results {
  max-height: 400px;
  overflow-y: auto;
}

.table-success {
  background-color: rgba(40, 167, 69, 0.1);
}

.table-danger {
  background-color: rgba(220, 53, 69, 0.1);
}
</style>